import React, { useState, useEffect } from 'react';
import { Heart, X, MapPin, Trash2 } from 'lucide-react';

/**
 * Favorites Panel - Shows user's saved venues
 * Can be accessed from the profile or a floating button
 */
const FavoritesPanel = ({ isOpen, onClose, onSelectVenue }) => {
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const savedFavorites = JSON.parse(localStorage.getItem('ablefam_favorites') || '[]');
      setFavorites(savedFavorites);
    }
  }, [isOpen]);

  const removeFavorite = (venueId) => {
    const updated = favorites.filter(fav => fav.id !== venueId);
    localStorage.setItem('ablefam_favorites', JSON.stringify(updated));
    setFavorites(updated);
  };

  const clearAllFavorites = () => {
    if (window.confirm('Remove all saved venues?')) {
      localStorage.setItem('ablefam_favorites', JSON.stringify([]));
      setFavorites([]);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="bg-pink-600 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <Heart size={24} className="text-white fill-white mr-2" />
            <h2 className="text-xl font-semibold text-white">Saved Venues</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-pink-700 p-2 rounded-full transition-colors"
            aria-label="Close favorites panel"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          {favorites.length === 0 ? (
            <div className="text-center py-8">
              <Heart size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No saved venues yet</h3>
              <p className="text-gray-500 text-sm">
                Click the heart icon on any venue to save it here for quick access.
              </p>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500">{favorites.length} saved venue{favorites.length !== 1 ? 's' : ''}</p>
                <button
                  onClick={clearAllFavorites}
                  className="text-sm text-red-600 hover:text-red-800 flex items-center"
                >
                  <Trash2 size={14} className="mr-1" />
                  Clear all
                </button>
              </div>
              
              <div className="space-y-3">
                {favorites.map((venue) => (
                  <div
                    key={venue.id}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-pink-300 transition-colors cursor-pointer"
                    onClick={() => {
                      if (onSelectVenue) onSelectVenue(venue.id);
                      onClose();
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-1">{venue.name}</h4>
                        <p className="text-xs text-gray-500 flex items-center">
                          <MapPin size={12} className="mr-1" />
                          Saved {formatDate(venue.savedAt)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFavorite(venue.id);
                        }}
                        className="text-gray-400 hover:text-red-500 p-1"
                        aria-label={`Remove ${venue.name} from favorites`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-pink-600 text-white py-2 px-4 rounded-lg hover:bg-pink-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default FavoritesPanel;
